import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorIcon from '@mui/icons-material/ErrorOutlineRounded';
import PendingIcon from '@mui/icons-material/PendingOutlined';
import MapIcon from "@mui/icons-material/RoomOutlined";

function StatusIcon({ status, lastSeen, expectedInterval, timezone = 'UTC' }) {
  const getStatusInfo = () => {
    if (!lastSeen || lastSeen === '1970-01-01T00:00:00Z') {
      return { 
        icon: <PendingIcon className="text-gray-400" />, 
        tooltip: 'No activity' 
      };
    }
    
    // Ensure timestamp is treated as UTC by appending Z if not present
    const timestampUTC = lastSeen.endsWith('Z') ? lastSeen : lastSeen + 'Z';
    const lastSeenDate = new Date(timestampUTC);
    const currentTime = new Date().getTime();
    const timeSinceLastSeen = currentTime - lastSeenDate.getTime();
    
    // Format date - only apply timezone conversion for non-UTC timezones
    let formattedLastSeenDate;
    if (timezone === 'UTC') {
      formattedLastSeenDate = lastSeenDate.toLocaleString(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'UTC'
      });
    } else {
      formattedLastSeenDate = lastSeenDate.toLocaleString(undefined, {
        timeZone: timezone,
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    }
    
    if (expectedInterval === null || expectedInterval === undefined) {
      return { 
        icon: <PendingIcon className="text-yellow-500" />, 
        tooltip: (
          <>
            Last seen: {formattedLastSeenDate}<br/>
            Expected Interval: Not set
          </>
        )
      };
    }
    
    const expectedLastSeenTime = expectedInterval * 1000;
    
    if (timeSinceLastSeen <= expectedLastSeenTime) {
      return { 
        icon: <CheckIcon className="text-green-600" />, 
        tooltip: (
          <>
            Last seen: {formattedLastSeenDate}<br/>
            Expected Interval: {expectedInterval} sec
          </>
        )
      };
    } else {
      return { 
        icon: <ErrorIcon className="text-red-600" />, 
        tooltip: (
          <>
            Last seen: {formattedLastSeenDate} (overdue)<br/>
            Expected Interval: {expectedInterval} sec
          </>
        )
      };
    }
  };
  
  const { icon, tooltip } = getStatusInfo();
  
  return (
    <Tooltip 
      title={tooltip} 
      arrow
      componentsProps={{ 
        tooltip: { 
          sx: { background: '#000' } 
        } 
      }}
    >
      <span>{icon}</span>
    </Tooltip>
  );
}

export default function LocationsTable({ locations, onLocationClick }) {
  const navigate = useNavigate();
  const [timezone, setTimezone] = useState(localStorage.getItem('crocus_timezone') || 'UTC');
  
  // Listen for timezone changes
  useEffect(() => {
    const handleTimezoneChange = (e) => {
      setTimezone(e.detail);
    };
    
    window.addEventListener('timezoneChange', handleTimezoneChange);
    return () => window.removeEventListener('timezoneChange', handleTimezoneChange);
  }, []);
  
  const handleDeviceClick = (e, device, location) => {
    // Only prevent default for left clicks (button 0)
    // Allow middle clicks and right clicks to work normally
    if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      navigate(`/device/${device.serialNumber}`, {
        state: { device, location },
      });
    }
  };
  
  return (
    <div className="mt-4">
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left w-[1%]">Status</th>
              <th className="px-3 py-2 text-left">Location</th>
              <th className="px-3 py-2 text-left">City</th>
              <th className="px-3 py-2 text-left">GPS</th>
              <th className="px-3 py-2 text-left">Devices</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((location) => (
              <tr
                key={location.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onLocationClick(location)}
              >
                <td className="px-3 py-2">
                  <StatusIcon
                    status={location.status || "no-data"}
                    lastSeen={location.lastSeen}
                    expectedInterval={3600}
                    timezone={timezone}
                  />
                </td>
                <td className="px-3 py-2">
                  <div>
                    <div className="font-medium">{location.name}</div>
                  </div>
                </td>
                <td className="px-3 py-2">{location.city}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center">
                    <MapIcon className="text-sky-500 mr-1" fontSize="small" />
                    {location.coordinates[0].toFixed(6)},{location.coordinates[1].toFixed(6)}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="text-xs">
                    {location.devices.map((device, i) => (
                      <React.Fragment key={device.serialNumber ?? i}>
                        <a
                          href={`/device/${device.serialNumber}`}
                          onClick={(e) => handleDeviceClick(e, device, location)}
                          onMouseDown={(e) => {
                            // Handle middle click (button 1)
                            if (e.button === 1) {
                              e.stopPropagation();
                            }
                          }}
                          className="text-blue-600 hover:underline whitespace-nowrap"
                        >
                          {device.serialNumber}
                        </a>
                        {i < location.devices.length - 1 && ", "}
                      </React.Fragment>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {locations.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  No locations match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}